import { Field, Panel, ReadOnlyBanner, UploadHint } from '@/components/admin/Field'
import { lumi } from '@/lib/lumi'
import { uploadNewsBannerMedia } from '@/lib/upload'
import type { NewsBanner } from '@/types/radio'
import {ImagePlus, Trash2} from 'lucide-react'
import { FormEvent, useState } from 'react'
import toast from 'react-hot-toast'

function nowIso() {
  return new Date().toISOString()
}

interface NewsBannersManagerProps {
  banners: NewsBanner[]
  onSaved: () => Promise<void>
  readOnly?: boolean
}

function NewsBannersManager({ banners, onSaved, readOnly = false }: NewsBannersManagerProps) {
  const empty: Partial<NewsBanner> = {
    title: '',
    mediaUrl: '',
    mediaType: 'image',
    linkUrl: '',
    weight: 1,
    active: true,
  }
  const [form, setForm] = useState<Partial<NewsBanner>>(empty)
  const [editingId, setEditingId] = useState('')

  const onUpload = async (file?: File) => {
    if (!file) return
    try {
      const result = await uploadNewsBannerMedia(file)
      setForm((prev) => ({ ...prev, mediaUrl: result.url, mediaType: result.mediaType }))
      toast.success(result.mediaType === 'video' ? 'Vídeo enviado.' : 'Banner enviado.')
    } catch (err) {
      console.error(err)
      toast.error(err instanceof Error ? err.message : 'Falha no envio do banner.')
    }
  }

  const save = async (event: FormEvent) => {
    event.preventDefault()
    if (readOnly) return
    if (!form.title?.trim() || !form.mediaUrl) {
      toast.error('Preencha o título e envie o ficheiro 790 × 150.')
      return
    }
    try {
      const payload = {
        title: form.title.trim(),
        mediaUrl: form.mediaUrl,
        mediaType: form.mediaType === 'video' ? 'video' : 'image',
        linkUrl: form.linkUrl || '',
        weight: Math.max(1, Number(form.weight || 1)),
        active: form.active !== false,
        updatedAt: nowIso(),
      }
      if (editingId) {
        await lumi.entities.news_banners.update(editingId, payload)
      } else {
        await lumi.entities.news_banners.create({
          ...payload,
          creator: 'admin',
          createdAt: nowIso(),
        })
      }
      toast.success('Banner entre matérias guardado.')
      setForm(empty)
      setEditingId('')
      await onSaved()
    } catch (err) {
      console.error(err)
      toast.error('Não foi possível guardar o banner.')
    }
  }

  const remove = async (id?: string) => {
    if (!id || readOnly) return
    try {
      await lumi.entities.news_banners.delete(id)
      toast.success('Banner removido.')
      await onSaved()
    } catch (err) {
      console.error(err)
      toast.error('Não foi possível remover.')
    }
  }

  return (
    <div className="grid gap-5 lg:grid-cols-2">
      {readOnly ? (
        <div className="lg:col-span-2">
          <ReadOnlyBanner />
        </div>
      ) : null}
      {readOnly ? null : (
        <Panel title={editingId ? 'Editar banner entre matérias' : 'Cadastrar banner entre matérias'}>
          <p className="mb-3 rounded-xl bg-[#edf3ff] px-3 py-2 text-xs leading-relaxed text-[#0b2ea8]">
            Estes anúncios aparecem de forma aleatória entre as matérias, respeitando o peso. Tamanho certo:{' '}
            <strong>790 × 150 px</strong>. Formatos: JPEG, PNG, GIF, MP4 ou WEBM.
          </p>
          <form onSubmit={save} className="grid gap-3">
            <Field label="Título interno" value={form.title || ''} onChange={(e) => setForm({ ...form, title: e.target.value })} required />
            <Field label="Link opcional" value={form.linkUrl || ''} onChange={(e) => setForm({ ...form, linkUrl: e.target.value })} placeholder="https://" />
            <Field
              label="Peso (aparece mais vezes)"
              type="number"
              min={1}
              value={String(form.weight || 1)}
              onChange={(e) => setForm({ ...form, weight: Number(e.target.value) })}
            />
            <label className="inline-flex items-center gap-2 text-sm font-semibold">
              <input type="checkbox" checked={form.active !== false} onChange={(e) => setForm({ ...form, active: e.target.checked })} />
              Visível entre as matérias
            </label>
            <label className="block">
              <span className="mb-1.5 inline-flex items-center gap-2 text-sm font-semibold text-slate-700">
                <ImagePlus size={16} /> Ficheiro 790 × 150
              </span>
              <input
                type="file"
                accept="image/jpeg,image/png,image/gif,image/webp,video/mp4,video/webm,.jpg,.jpeg,.png,.gif,.webp,.mp4,.webm"
                onChange={(e) => void onUpload(e.target.files?.[0])}
              />
              <UploadHint size="790 × 150 px" ratio="790:150" tip="Banner horizontal entre matérias." formats="JPEG, PNG, GIF, MP4 ou WEBM." />
              {form.mediaUrl ? (
                form.mediaType === 'video' ? (
                  <video src={form.mediaUrl} className="mt-2 aspect-[790/150] w-full rounded-xl object-cover" muted controls />
                ) : (
                  <img src={form.mediaUrl} alt="" className="mt-2 aspect-[790/150] w-full rounded-xl object-cover" />
                )
              ) : null}
            </label>
            <button className="rounded-full bg-[#0b2ea8] py-3 font-bold text-white">Guardar banner</button>
          </form>
        </Panel>
      )}
      <Panel title="Banners entre matérias">
        <div className="space-y-3">
          {banners.map((item) => (
            <article key={item._id} className="overflow-hidden rounded-xl border border-slate-200">
              {item.mediaType === 'video' ? (
                <video src={item.mediaUrl} className="aspect-[790/150] w-full object-cover" muted />
              ) : (
                <img src={item.mediaUrl} alt={item.title} className="aspect-[790/150] w-full object-cover" />
              )}
              <div className="flex items-center justify-between gap-3 p-3">
                <div>
                  <p className="font-bold">{item.title}</p>
                  <p className="text-xs text-slate-500">
                    {item.mediaType === 'video' ? 'Vídeo' : 'Imagem'} · peso {item.weight || 1}
                    {item.active === false ? ' · oculto' : ''}
                  </p>
                </div>
                {readOnly ? null : (
                  <div className="flex gap-3">
                    <button
                      type="button"
                      className="text-sm font-bold text-[#0b2ea8]"
                      onClick={() => {
                        setForm(item)
                        setEditingId(item._id || '')
                      }}
                    >
                      Editar
                    </button>
                    <button type="button" onClick={() => void remove(item._id)} className="text-red-500">
                      <Trash2 size={16} />
                    </button>
                  </div>
                )}
              </div>
            </article>
          ))}
          {!banners.length ? <p className="text-sm text-slate-500">Ainda não há banners entre matérias.</p> : null}
        </div>
      </Panel>
    </div>
  )
}

export default NewsBannersManager
